// ============================================================================
// File: src/miniapps/lexlinkflower/lexlinkflower.js
// LinkFlower React Component (FIXED MOBILE BG FLICKER + AUTH GATE)
// Last Updated: 2025-07-22
// ============================================================================
import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { MoreVertical, Edit } from 'lucide-react';
import debounce from 'lodash.debounce';
import LexLoginSignupPopup from '../lex-loginsignup-popup/lex-loginsignup-popup';

import { useAuth } from '../../user/AuthContext';
import './lexlinkflower.css';

import {
  listenLinkFlowerProfile,
  saveLinkFlowerProfile,
  listenLinks,
  addLink,
  updateLink,
  deleteLink as deleteLinkDoc,
  reorderLinks,
  listenBanners,
  uploadBannerFile,
  deleteBannerDoc,
  reorderBanners, // 预留
  updateSlugTransaction,
  db,
} from '../../firebase';

// 背景模板 CSS 类
const backgroundTemplates = [
  'gradient-holo-chrome',
  'gradient-rainbow-fusion',
  'gradient-neon-beacon',
  'gradient-electric-arc',
  'gradient-cosmic-vortex',
  'gradient-laser-grid',
  'gradient-aurora-flare',
  'gradient-pulse-fire',
  'gradient-glitch-arc',
  'gradient-ultraviolet-shift',
  'gradient-hyperdrive',
  'gradient-polar-shift',
  'gradient-galactic-spark',
  'gradient-lava-ripple',
];

// slug -> uid
async function resolveUidBySlug(slug) {
  if (!slug) return null;
  try {
    const snap = await getDoc(doc(db, 'slugs', slug));
    return snap.exists() ? snap.data().uid : null;
  } catch {
    return null;
  }
}

const params = new URLSearchParams(window.location.search);
const shareCards = (params.get('cards') || '').split(',').filter(Boolean);

export default function LexLinkFlower({ initialUsername = 'YourName' }) {
  const { slug: routeSlug } = useParams();
  const { user } = useAuth();

  // ⚠️ 去掉自动 reload，会造成“闪” & 状态丢失
  // useEffect(() => {...}, []) 已移除

  // 解析 slug 获得目标 uid
  const [resolvedUid, setResolvedUid] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const viewingOwnMode = !routeSlug; // 根路径模式
  const isOwner = !!user && resolvedUid && user.uid === resolvedUid;

  const effectiveUid = resolvedUid || (viewingOwnMode && user?.uid) || null;
  const canPersist = !!effectiveUid && !!user;
  const canEditUI = viewingOwnMode || isOwner;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!routeSlug) {
        if (user?.uid) setResolvedUid(user.uid);
        else setResolvedUid(null);
        setNotFound(false);
        return;
      }
      setResolvedUid(null);
      setNotFound(false);
      const uid = await resolveUidBySlug(routeSlug.toLowerCase());
      if (cancelled) return;
      if (!uid) setNotFound(true);
      else setResolvedUid(uid);
    })();
    return () => {
      cancelled = true;
    };
  }, [routeSlug, user?.uid]);

  // 登录提示
  const [loginPromptVisible, setLoginPromptVisible] = useState(false);

  // 写操作 gating
  const requirePersist = useCallback(
    (fn) => {
      if (!canPersist) {
        if (!user) setLoginPromptVisible(true);
        return;
      }
      fn && fn();
    },
    [canPersist, user]
  );

  // 全局点击拦截 (事件委托)
  const globalAuthGate = useCallback(
    (e) => {
      if (shareCards.length > 0 || user) return;
      const target = e.target.closest('[data-require-auth="true"]');
      if (!target) return;
      e.preventDefault();
      e.stopPropagation();
      setLoginPromptVisible(true);
    },
    [user] // shareCards.length 是常量，不需要进依赖
  );

  // 输入域 focus 防护
  const authFocusGuard = useCallback(
    (e) => {
      if (user) return;
      e.target.blur();
      setLoginPromptVisible(true);
    },
    [user]
  );

  // Profile
  const [profile, setProfile] = useState(null);
  useEffect(() => {
    if (effectiveUid) {
      const unsub = listenLinkFlowerProfile(effectiveUid, (p) => {
        setProfile(
          p || {
            slug: '',
            bio: '',
            templateIndex: 0,
            activeBanner: 0,
          }
        );
      });
      return () => unsub && unsub();
    }
    if (viewingOwnMode && !user) {
      setProfile({
        slug: '',
        bio: '',
        templateIndex: 0,
        activeBanner: 0,
      });
    } else {
      setProfile(null);
    }
  }, [effectiveUid, viewingOwnMode, user]);

  // Slug
  const [slugInput, setSlugInput] = useState('');
  const [slugError, setSlugError] = useState(null);
  useEffect(() => {
    if (profile) setSlugInput(profile.slug || '');
  }, [profile?.slug]);

  const commitSlug = useMemo(
    () =>
      debounce(async (value) => {
        if (!canPersist || !effectiveUid) return;
        if (!value) return;
        if (!/^[a-z0-9-]{3,30}$/.test(value)) {
          setSlugError('3-30 chars: a-z, 0-9, -');
          return;
        }
        try {
          await updateSlugTransaction(effectiveUid, value);
          setSlugError(null);
        } catch (e) {
          if (e?.message?.toLowerCase?.().includes('taken')) {
            setSlugError('This suffix is taken');
          } else setSlugError(e.message || 'Slug update failed');
        }
      }, 600),
    [canPersist, effectiveUid]
  );

  const handleSlugChange = (e) => {
    const v = e.target.value.toLowerCase();
    setSlugInput(v);
    setSlugError(null);
    if (canPersist) commitSlug(v);
  };

  // Bio
  const [bioDraft, setBioDraft] = useState('');
  const bioRef = useRef(null);

  useEffect(() => {
    if (profile) setBioDraft(profile.bio || '');
  }, [profile?.bio]);

  useEffect(() => {
    const ta = bioRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${ta.scrollHeight}px`;
  }, [bioDraft]);

  const debouncedSaveBio = useMemo(
    () =>
      debounce((text) => {
        if (!canPersist || !effectiveUid) return;
        saveLinkFlowerProfile(effectiveUid, { bio: text });
      }, 800),
    [canPersist, effectiveUid]
  );

  const handleBioChange = (e) => {
    const ta = e.target;
    ta.style.height = 'auto';
    ta.style.height = `${ta.scrollHeight}px`;
    setBioDraft(ta.value);
    if (canPersist) debouncedSaveBio(ta.value);
  };

  // Template
  const switchTemplate = () =>
    requirePersist(() => {
      if (!profile) return;
      const next = (profile.templateIndex + 1) % backgroundTemplates.length;
      saveLinkFlowerProfile(effectiveUid, { templateIndex: next });
    });

  // Banners
  const [banners, setBanners] = useState([]);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!effectiveUid) {
      setBanners([]);
      return;
    }
    const unsub = listenBanners(effectiveUid, setBanners);
    return () => unsub && unsub();
  }, [effectiveUid]);

  const handleAddBannerClick = () =>
    requirePersist(() => fileInputRef.current?.click());

  const handleBannerChange = (e) =>
    requirePersist(async () => {
      const file = e.target.files[0];
      if (!file) return;
      try {
        await uploadBannerFile(effectiveUid, file);
        saveLinkFlowerProfile(effectiveUid, {
          activeBanner: banners.length,
        });
      } catch (err) {
        console.warn('Upload banner failed', err);
        alert('Banner upload failed');
      } finally {
        e.target.value = '';
      }
    });

  const deleteBanner = () =>
    requirePersist(async () => {
      if (!profile) return;
      const idx = profile.activeBanner;
      const target = banners[idx];
      if (!target) return;
      try {
        await deleteBannerDoc(effectiveUid, target.id);
        const newIndex = Math.max(0, idx - 1);
        saveLinkFlowerProfile(effectiveUid, { activeBanner: newIndex });
      } catch (e) {
        console.warn('Delete banner failed', e);
        alert('Delete banner failed');
      }
    });

  const setActiveBanner = (i) =>
    requirePersist(() => {
      saveLinkFlowerProfile(effectiveUid, { activeBanner: i });
    });

  // Links
  const [links, setLinks] = useState([]);
  useEffect(() => {
    if (!effectiveUid) {
      if (canPersist) setLinks([]);
      return;
    }
    const unsub = listenLinks(effectiveUid, setLinks);
    return () => unsub && unsub();
  }, [effectiveUid, canPersist]);

  const [adding, setAdding] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newUrl, setNewUrl] = useState('');

  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editUrl, setEditUrl] = useState('');

  const beginAdd = () => {
    if (!canEditUI) return;
    setAdding(true);
    setNewTitle('');
    setNewUrl('');
  };

  const saveNew = () =>
    requirePersist(async () => {
      if (!newTitle.trim() || !newUrl.trim()) {
        alert('Please enter both title and URL.');
        return;
      }
      try {
        await addLink(effectiveUid, {
          title: newTitle.trim(),
          url: newUrl.trim(),
        });
        setAdding(false);
      } catch (e) {
        console.warn('Add link failed', e);
        alert('Add link failed');
      }
    });

  const cancelNew = () => setAdding(false);

  const startEdit = (link) => {
    if (!canEditUI) return;
    setEditingId(link.id);
    setEditTitle(link.title);
    setEditUrl(link.url);
  };

  const saveEdit = (id) =>
    requirePersist(async () => {
      if (!editTitle.trim() || !editUrl.trim()) {
        alert('Title and URL required');
        return;
      }
      try {
        await updateLink(effectiveUid, id, {
          title: editTitle.trim(),
          url: editUrl.trim(),
        });
        setEditingId(null);
      } catch (e) {
        console.warn('Update link failed', e);
        alert('Update link failed');
      }
    });

  const cancelEdit = () => setEditingId(null);

  const deleteLink = (id) =>
    requirePersist(async () => {
      if (!window.confirm('Delete this link?')) return;
      try {
        await deleteLinkDoc(effectiveUid, id);
      } catch (e) {
        console.warn('Delete link failed', e);
        alert('Delete link failed');
      }
    });

  // 拖拽排序
  const dragSrcIndex = useRef(null);
  const onDragStart = (e, idx) => {
    if (!canEditUI || !canPersist) return;
    dragSrcIndex.current = idx;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(idx));
  };
  const onDragOver = (e) => e.preventDefault();
  const onDrop = (e, destIndex) =>
    requirePersist(async () => {
      e.preventDefault();
      const srcIndex = dragSrcIndex.current;
      if (srcIndex === null || srcIndex === destIndex) return;
      const arr = [...links];
      const [moved] = arr.splice(srcIndex, 1);
      arr.splice(destIndex, 0, moved);
      dragSrcIndex.current = null;
      setLinks(arr);
      try {
        await reorderLinks(effectiveUid, arr.map((l) => l.id));
      } catch (err) {
        console.warn('Reorder failed', err);
        alert('Reorder failed (will re-sync)');
      }
    });

  // Share
  const effectiveSlug = profile?.slug || routeSlug || '';
  const shareUrlFull = effectiveSlug
    ? `https://www.lextrackai.com/linkflower/${effectiveSlug}`
    : 'Set a slug';
  const [shareOpen, setShareOpen] = useState(false);

  const copyShare = async () => {
    try {
      await navigator.clipboard.writeText(shareUrlFull);
      alert('URL copied!');
    } catch {
      alert('Copy failed');
    }
  };

  // 清理 debounce
  useEffect(() => {
    return () => {
      commitSlug.cancel();
      debouncedSaveBio.cancel();
    };
  }, [commitSlug, debouncedSaveBio]);

  const templateIndex = profile?.templateIndex || 0;
  const activeBannerIndex = profile?.activeBanner || 0;

  // Not Found
  if (notFound) {
    return (
      <div className="llf-linktree-container gradient-polar-shift">
        <h2>Profile not found</h2>
        <p>This LinkFlower page does not exist</p>
      </div>
    );
  }

  // Public slug 加载中
  if (routeSlug && resolvedUid === null && !notFound) {
    return (
      <div className="llf-linktree-container gradient-laser-grid">
        <p style={{ padding: '2rem', opacity: 0.8 }}>Loading...</p>
      </div>
    );
  }

  // MAIN RENDER
  return (
    <>
      {/* 背景层 */}
      <div className={`lf-bg ${backgroundTemplates[templateIndex]}`} />

      {/* 滚动层 */}
      <div id="lf-scroll">
        <div className="llf-linktree-container" onClick={globalAuthGate}>
          {/* 登录提示 */}
          <LexLoginSignupPopup
            loginPromptVisible={loginPromptVisible}
            setLoginPromptVisible={setLoginPromptVisible}
          />

          {/* Controls */}
          <div className="llf-controls">
            <label className="llf-slug-label">
              Suffix:
              {canEditUI ? (
                <input
                  className={`llf-slug-input ${
                    slugError ? 'llf-slug-error' : ''
                  }`}
                  value={slugInput}
                  onChange={handleSlugChange}
                  placeholder="your-name"
                  data-require-auth="true"
                  onFocus={authFocusGuard}
                />
              ) : effectiveSlug ? (
                <div className="llf-slug-readonly">@{effectiveSlug}</div>
              ) : (
                <div className="llf-slug-readonly muted">No slug</div>
              )}
            </label>
            {slugError && (
              <span className="llf-slug-error-msg">{slugError}</span>
            )}

            <button
              className="llf-share-button"
              onClick={() => setShareOpen((v) => !v)}
              data-require-auth="true"
            >
              Share
            </button>
            {shareOpen && (
              <div className="llf-share-dropdown" data-require-auth="true">
                <div className="llf-share-url">{shareUrlFull}</div>
                <button
                  className="llf-copy-button"
                  onClick={copyShare}
                  data-require-auth="true"
                >
                  Copy
                </button>
              </div>
            )}

            <button
              className="llf-menu-button"
              onClick={switchTemplate}
              aria-label="Switch background template"
              disabled={!canEditUI}
              data-require-auth="true"
            >
              ⋯
            </button>
          </div>

          {/* Banner */}
          <div className="llf-banner-wrapper">
            {banners.length > 0 ? (
              <>
                <img
                  src={
                    banners[activeBannerIndex]
                      ? banners[activeBannerIndex].imageUrl
                      : banners[0].imageUrl
                  }
                  className="llf-banner"
                  alt="Banner"
                />
                {canEditUI && canPersist && (
                  <button
                    className="llf-delete-banner"
                    onClick={deleteBanner}
                    aria-label="Delete banner"
                    data-require-auth="true"
                  >
                    ×
                  </button>
                )}
              </>
            ) : (
              <label
                className="llf-banner-placeholder"
                onClick={
                  canEditUI && canPersist ? handleAddBannerClick : undefined
                }
                data-require-auth="true"
                onFocus={authFocusGuard}
              >
                {canEditUI
                  ? canPersist
                    ? 'Upload Banner'
                    : (
                        <>
                          This is Link Flower
                          <br />
                          <br />
                          Upload your banner here
                        </>
                      )
                  : 'No banner'}
              </label>
            )}

            {canEditUI && canPersist && (
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleBannerChange}
              />
            )}

            <div className="llf-banner-dots">
              {banners.map((b, i) => (
                <button
                  key={b.id}
                  className={`llf-dot ${
                    i === activeBannerIndex ? 'active' : ''
                  }`}
                  onClick={() =>
                    canEditUI && canPersist ? setActiveBanner(i) : null
                  }
                  disabled={!canEditUI || !canPersist}
                />
              ))}

              {canEditUI && (
                <button
                  className="llf-dot add-dot"
                  onClick={handleAddBannerClick}
                  data-require-auth="true"
                >
                  +
                </button>
              )}
            </div>
          </div>

          {/* Bio */}
          <textarea
            ref={bioRef}
            className="llf-bio-input"
            style={{ height: 'auto', overflow: 'hidden', resize: 'none' }}
            placeholder={
              canEditUI
                ? canPersist
                  ? 'Enter your bio…'
                  : 'Enter your bio…'
                : 'No bio'
            }
            value={bioDraft}
            onChange={handleBioChange}
            disabled={!canEditUI}
            data-require-auth="true"
            onFocus={authFocusGuard}
          />

          {/* Links */}
          <div className="llf-links-list">
            {links.map((link, idx) => {
              const editing = editingId === link.id;
              return (
                <div
                  key={link.id}
                  className={`llf-link-item ${editing ? 'editing' : ''}`}
                  draggable={canEditUI && canPersist}
                  onDragStart={(e) => onDragStart(e, idx)}
                  onDragOver={onDragOver}
                  onDrop={(e) => onDrop(e, idx)}
                >
                  <MoreVertical className="llf-drag-handle" />
                  {editing ? (
                    <>
                      <input
                        className="llf-edit-input"
                        value={editTitle}
                        onChange={(e) => setEditTitle(e.target.value)}
                        placeholder="Title"
                        data-require-auth="true"
                        onFocus={authFocusGuard}
                      />
                      <input
                        className="llf-edit-input"
                        value={editUrl}
                        onChange={(e) => setEditUrl(e.target.value)}
                        placeholder="URL"
                        data-require-auth="true"
                        onFocus={authFocusGuard}
                      />
                      <button
                        className="llf-save-button"
                        onClick={() => saveEdit(link.id)}
                        data-require-auth="true"
                      >
                        Save
                      </button>
                      <button
                        className="llf-cancel-button"
                        onClick={cancelEdit}
                        data-require-auth="true"
                      >
                        Cancel
                      </button>
                      <button
                        className="llf-delete-button"
                        onClick={() => deleteLink(link.id)}
                        data-require-auth="true"
                      >
                        ×
                      </button>
                    </>
                  ) : (
                    <>
                      <a
                        href={link.url || '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="llf-link-button"
                        onClick={(e) => {
                          if (!link.url) e.preventDefault();
                        }}
                      >
                        {link.title || 'Untitled'}
                      </a>

                      {canEditUI && (
                        <>
                          <button
                            className="llf-edit-button"
                            onClick={() => startEdit(link)}
                            aria-label="Edit link"
                            data-require-auth="true"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            className="llf-delete-button"
                            onClick={() => deleteLink(link.id)}
                            data-require-auth="true"
                          >
                            ×
                          </button>
                        </>
                      )}
                    </>
                  )}
                </div>
              );
            })}

            {/* Add Link */}
            {canEditUI &&
              (adding ? (
                <div className="llf-link-item editing">
                  <MoreVertical className="llf-drag-handle" />
                  <input
                    className="llf-edit-input"
                    value={newTitle}
                    onChange={(e) => setNewTitle(e.target.value)}
                    placeholder="Title"
                    data-require-auth="true"
                    onFocus={authFocusGuard}
                  />
                  <input
                    className="llf-edit-input"
                    value={newUrl}
                    onChange={(e) => setNewUrl(e.target.value)}
                    placeholder="URL"
                    data-require-auth="true"
                    onFocus={authFocusGuard}
                  />
                  <button
                    className="llf-save-button"
                    onClick={saveNew}
                    data-require-auth="true"
                  >
                    Add
                  </button>
                  <button
                    className="llf-cancel-button"
                    onClick={cancelNew}
                    data-require-auth="true"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  className="llf-link-button llf-add-link-button"
                  onClick={beginAdd}
                  data-require-auth="true"
                >
                  + Add Link
                </button>
              ))}
          </div>

          <div className="llf-bottom-row" />
        </div>
      </div>
    </>
  );
}
